/**
 * Storage Service
 * 
 * Handles file uploads via backend API to AWS S3.
 * Uses Supabase user ID (from auth.users) for file organization.
 * Stores only AWS S3 URLs in Supabase database.
 */

import { getSupabaseClient } from './supabase';
import { Asset } from './assets';
import { API_CONFIG, API_ENDPOINTS } from '../utils/constants';
import { API_URL } from '../config/apiConfig';

export interface UploadResult {
  path: string;
  url: string;
  userId: string;
  isAnonymous: boolean;
  asset: Asset; // Asset record in database
}

// Helper functions for production-ready logging
// Only verbose logs in development, errors/warnings always logged
const isDev = process.env.NODE_ENV === 'development';
const log = (...args: any[]) => {
  if (isDev) console.log(...args);
};
const logError = (...args: any[]) => console.error(...args);
const logWarn = (...args: any[]) => console.warn(...args);

/**
 * Upload an image file via backend API to AWS S3
 * 
 * Uses the current Supabase user ID (from auth.users) for file organization.
 * Works with both anonymous and permanent users.
 * Stores only the AWS S3 URL in Supabase database.
 * 
 * @param file - The image file to upload
 * @param folder - Optional folder path (defaults to 'img-assets')
 * @param prompt - Optional prompt text to store with the asset
 * @returns Upload result with path, URL, user ID, and anonymous status
 */

export async function uploadImageToStorage(
  file: File,
  folder?: string,
  prompt?: string
): Promise<UploadResult> {
  log('🚀 [Storage] ===== UPLOAD IMAGE START =====');
  log('📁 [Storage] File received:', file.name);
  log('📏 [Storage] File size:', (file.size / 1024).toFixed(2), 'KB');
  log('📦 [Storage] File type:', file.type);
  if (prompt) {
    const promptPreview = prompt.length > 100 
      ? prompt.substring(0, 100) + '...' 
      : prompt;
    log('💬 [Storage] Prompt provided:', promptPreview);
    log('💬 [Storage] Prompt length:', prompt.length, 'characters');
  }
  
  const supabase = getSupabaseClient();
  log('✅ [Storage] Supabase client initialized');
  
  // CRITICAL: Get FRESH session to obtain access token for backend API
  // Backend needs the token to create asset records with proper user_id
  // We get a fresh session at call time to ensure we have the latest token
  log('🔍 [Storage] Getting FRESH Supabase session for access token...');
  const { data: { session }, error: sessionError } = await supabase.auth.getSession();
  
  if (sessionError) {
    logError('❌ [Storage] Error getting session:', sessionError.message);
    throw new Error('Failed to get authentication session. Please sign in again.');
  }
  
  if (!session) {
    logError('❌ [Storage] No session found - user must be authenticated');
    throw new Error('User must be authenticated before uploading. Please sign in first.');
  }
  
  // Try to get current user
  log('🔍 [Storage] Checking for authenticated user...');
  const { data: { user }, error: userError } = await supabase.auth.getUser();
  
  if (userError) {
    logError('❌ [Storage] Error getting user:', userError.message);
    throw new Error('Failed to get user information. Please sign in again.');
  }
  
  if (!user) {
    logError('❌ [Storage] No user found - user must be authenticated');
    throw new Error('User must be authenticated before uploading. Please sign in first.');
  }
  
  // User is authenticated and we have a session
  log('✅ [Storage] User authenticated');
  log('👤 [Storage] Supabase User ID:', user.id);
  log('🔑 [Storage] Is Anonymous:', user.is_anonymous ?? false);
  log('📧 [Storage] User Email:', user.email || 'N/A');
  log('🎫 [Storage] Access token present:', !!session.access_token);
  
  if (!session.access_token) {
    logError('❌ [Storage] CRITICAL: Session exists but access_token is missing');
    throw new Error('Access token not available. Please sign in again.');
  }
  
  const userId = user.id;
  const isAnonymous = user.is_anonymous ?? false;
  const accessToken = session.access_token;
  
  const uploadFolder = folder || 'img-assets';
  
  log('📂 [Storage] Upload folder:', uploadFolder);
  log('⏳ [Storage] Starting upload via backend API...');
  log('🌐 [Storage] Backend URL:', API_CONFIG.BASE_URL);
  log('📞 [Storage] Endpoint:', API_ENDPOINTS.UPLOAD.IMAGE);

  // Upload file via backend API (backend handles S3 upload)
  let uploadResponse: {
    success: boolean;
    url: string;
    key: string;
    bucket: string;
    asset_id?: string;
  };
  
  try {
    log('📤 [Storage] Preparing FormData...');
    const formData = new FormData();
    formData.append('file', file);
    formData.append('user_id', userId);
    if (uploadFolder) {
      formData.append('folder', uploadFolder);
    }
    // Send prompt to backend API so it can be stored in the asset record
    if (prompt && prompt.trim()) {
      formData.append('prompt', prompt.trim());
      log('💬 [Storage] Prompt included in upload request');
    }
    
    log('📤 [Storage] Sending request to backend...');
    const uploadUrl = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.UPLOAD.IMAGE}`;
    log('📡 [Storage] Full URL:', uploadUrl);
    
    // CRITICAL: Include Authorization header with Supabase access token
    // Backend needs this to create asset records with proper user_id and RLS policies
    const headers: HeadersInit = {};
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
      log('🎫 [Storage] Authorization header included with access token');
    } else {
      logError('❌ [Storage] WARNING: No access token available for upload request');
      throw new Error('Access token is required for upload. Please sign in again.');
    }
    
    const response = await fetch(uploadUrl, {
      method: 'POST',
      headers: headers,
      body: formData,
    });
    
    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || errorMessage;
      } catch (e) {
        // If response is not JSON, use status text
        const text = await response.text().catch(() => '');
        if (text) {
          errorMessage = text;
        }
      }
      throw new Error(`Failed to upload image: ${errorMessage}`);
    }
    
    uploadResponse = await response.json();
    
    // Log full backend response for debugging
    log('📦 [Storage] Backend response received:', JSON.stringify(uploadResponse, null, 2));
    
    // Validate that we got the essential data
    if (!uploadResponse.url) {
      logError('❌ [Storage] CRITICAL: Backend upload succeeded but no URL returned');
      logError('❌ [Storage] Backend response:', uploadResponse);
      throw new Error('Backend upload succeeded but no URL returned');
    }
    log('✅ [Storage] Backend upload successful');
    log('🔗 [Storage] S3 URL:', uploadResponse.url);
    log('🔑 [Storage] S3 Key:', uploadResponse.key || 'N/A');
    log('🆔 [Storage] Asset ID from backend:', uploadResponse.asset_id || 'NOT PROVIDED');
    
    // CRITICAL: Backend MUST return a valid UUID asset_id
    // Generation will fail if asset_id is missing or not a UUID
    if (!uploadResponse.asset_id) {
      logError('❌ [Storage] CRITICAL: Backend did not return asset_id');
      logError('❌ [Storage] Backend response body:', JSON.stringify(uploadResponse, null, 2));
      logError('❌ [Storage] Backend response keys:', Object.keys(uploadResponse));
      throw new Error('Backend upload succeeded but did not return asset_id. Cannot proceed with generation.');
    }
    
    // Validate asset_id is a valid UUID format
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(uploadResponse.asset_id)) {
      logError('❌ [Storage] CRITICAL: Backend returned invalid asset_id format:', uploadResponse.asset_id);
      logError('❌ [Storage] Asset ID must be a valid UUID (Supabase format)');
      logError('❌ [Storage] Backend response:', uploadResponse);
      throw new Error(`Backend returned invalid asset_id format: ${uploadResponse.asset_id}. Expected UUID format.`);
    }
    
    log('✅ [Storage] Asset ID validated as UUID:', uploadResponse.asset_id);
    log('✅ [Storage] Backend successfully created asset record with ID:', uploadResponse.asset_id);
    
    // IMPORTANT: The backend API should create ONE asset record in Supabase
    // If duplicates are appearing, check:
    // 1. Is the backend API being called multiple times?
    // 2. Is the backend creating duplicates internally?
    // 3. Check backend logs for duplicate asset creation
    if (uploadResponse.asset_id) {
      log('📝 [Storage] Backend should have created asset record with ID:', uploadResponse.asset_id);
      log('⚠️ [Storage] If you see duplicate assets, check backend logs for duplicate creation');
    }
  } catch (uploadError: any) {
    logError('❌ [Storage] Backend upload failed:', uploadError);
    logError('❌ [Storage] Error type:', typeof uploadError);
    logError('❌ [Storage] Error message:', uploadError?.message);
    
    throw new Error(`Failed to upload image: ${uploadError?.message || 'Unknown error'}`);
  }
  
  // Backend handles all asset creation and status management
  // CRITICAL: Always update the asset with prompt if provided, even if backend should have stored it
  // This ensures the prompt is definitely in the database
  if (uploadResponse.asset_id && prompt && prompt.trim()) {
    log('💾 [Storage] Prompt was provided, ensuring it is stored in asset record...');
    log('🆔 [Storage] Asset ID to update:', uploadResponse.asset_id);
    log('💬 [Storage] Prompt to store:', prompt.trim().substring(0, 50) + '...');
    
    let promptStored = false;
    
    // Try backend API first (bypasses RLS)
    const API_BASE_URL = API_URL;
    try {
      log('🌐 [Storage] Attempting to update asset via backend API...');
      const updateResponse = await fetch(`${API_BASE_URL}/api/assets/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: uploadResponse.asset_id,  // Backend expects 'id', not 'asset_id'
          prompt: prompt.trim(),
        }),
      });
      
      if (updateResponse.ok) {
        await updateResponse.json(); // Response confirmation
        log('✅ [Storage] Prompt stored via backend API');
        promptStored = true;
      } else {
        const error = await updateResponse.json().catch(() => ({ error: 'Backend update failed' }));
        logWarn('⚠️ [Storage] Backend API update failed:', error);
        logWarn('⚠️ [Storage] Status:', updateResponse.status, updateResponse.statusText);
      }
    } catch (apiError) {
      logWarn('⚠️ [Storage] Backend API not available or failed:', apiError);
    }
    
    // If backend API failed, try direct Supabase update (may fail due to RLS, but worth trying)
    if (!promptStored) {
      try {
        log('🔄 [Storage] Attempting direct Supabase update as fallback...');
        const { updateAsset } = await import('./assets');
        await updateAsset(uploadResponse.asset_id, {
          prompt: prompt.trim(),
        });
        log('✅ [Storage] Prompt stored via direct Supabase update');
        promptStored = true;
      } catch (supabaseError) {
        logError('❌ [Storage] Direct Supabase update also failed:', supabaseError);
        logError('❌ [Storage] This is likely due to RLS policies. Backend should have stored the prompt.');
        logError('❌ [Storage] Prompt may not be stored in the asset record.');
        // Don't throw - continue with upload, but log the error clearly
      }
    }
    
    if (!promptStored) {
      logError('❌ [Storage] CRITICAL: Failed to store prompt in asset record after all attempts!');
      logError('❌ [Storage] Asset ID:', uploadResponse.asset_id);
      logError('❌ [Storage] Prompt:', prompt.trim());
    }
  } else if (uploadResponse.asset_id && (!prompt || !prompt.trim())) {
    log('ℹ️ [Storage] No prompt provided, skipping prompt storage');
  } else if (!uploadResponse.asset_id) {
    logWarn('⚠️ [Storage] No asset_id returned from backend, cannot store prompt');
  }
  
  // Create asset object from backend response
  // CRITICAL: asset_id is guaranteed to exist and be a valid UUID at this point
  const asset: Asset = {
    id: uploadResponse.asset_id, // Always a valid UUID (validated above)
    user_id: userId,
    type: 'image',
    operation_type: 'upload',
    status: 'completed',
    s3_url: uploadResponse.url,
    prompt: prompt && prompt.trim() ? prompt.trim() : undefined,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  } as Asset;
  
  log('✅ [Storage] Asset record created by backend:', uploadResponse.asset_id);
  log('✅ [Storage] Asset ID is valid UUID:', uploadResponse.asset_id);
  if (prompt && prompt.trim()) {
    log('💬 [Storage] Prompt should be stored in asset record');
  }
  
  log('✅ [Storage] ===== UPLOAD COMPLETE =====');
  log('🆔 [Storage] Final Asset ID:', asset.id);

  return {
    path: uploadResponse.key,
    url: uploadResponse.url,
    userId: userId,
    isAnonymous,
    asset: asset,
  };
}

/**
 * List images for the current user
 * This now queries the assets table instead of listing S3 files
 * 
 * @returns Array of asset records
 */
export async function listUserImages() {
  const { getUserAssets } = await import('./assets');
  return getUserAssets('image');
}

